<template>
  <div :class="$style.root">
    <button :class="$style.root__close">
      <IconClose @click="$emit('close')" />
    </button>
    <header :class="$style.root__header">
      <p :class="$style.header__text">検索条件を保存</p>
    </header>
    <div :class="$style.root__content">
      <AppPopover v-show="isFocused" :class="$style.content__popover">
        <ul :class="$style.popover">
          <li
            v-for="message in validationMessages"
            :key="message"
            :class="[
              $style.popover__message,
              $style['popover__message--invalid'],
            ]"
          >
            <IconExclamationCircle :class="$style.popover__icon" />
            <p>{{ message }}</p>
          </li>
          <li
            :class="[
              $style.popover__message,
              $style['popover__message--valid'],
            ]"
            v-show="!hasValidationMessages"
          >
            <IconCheckCircle :class="$style.popover__icon" />
            <p>設定可能な検索条件名です</p>
          </li>
        </ul>
      </AppPopover>
      <DolphinTextInput
        :class="$style.content__input"
        placeholder="例: 富裕層 (子供あり)"
        v-model="inputText"
        @focus="isFocused = true"
        @blur="isFocused = false"
        name="text"
      />
      <FlatButton
        variant="primary"
        size="medium"
        :class="$style.content__button"
        :disabled="!isValidText"
        @click="submit"
        >検索条件を保存</FlatButton
      >
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import DolphinTextInput from '@/components/atoms/DolphinTextInput.vue'
import IconClose from '@/components/icons/IconClose.vue'
import AppPopover from '@/components/atoms/AppPopover.vue'
import IconCheckCircle from '@/components/icons/IconCheckCircle.vue'
import IconExclamationCircle from '@/components/icons/IconExclamationCircle.vue'
import FlatButton from '@/components/atoms/FlatButton.vue'

const text = ref('')
const isFocused = ref(false)

const emit = defineEmits<{
  (event: 'close'): void
  (event: 'submit', text: string): void
}>()

const inputText = computed({
  get(): string {
    return text.value
  },
  set(inputText: string): void {
    text.value = inputText
  },
})

const submit = () => {
  emit('submit', text.value)
  emit('close')
}

const isValidText = computed(() => {
  return isValidLengthText.value && isNotJustWhitespace.value
})

const isValidLengthText = computed(() => {
  return inputText.value.length > 0
})

const isNotJustWhitespace = computed(() => {
  return inputText.value.trim().length > 0
})

const hasValidationMessages = computed(() => {
  return validationMessages.value.length > 0
})

const validationMessages = computed(() =>
  [
    {
      invalid: !isValidLengthText.value,
      message: '1文字以上入力してください',
    },
    {
      invalid: !isNotJustWhitespace.value,
      message: 'スペースなど空白文字のみを使用することはできません',
    },
  ]
    .filter(({ invalid }) => invalid)
    .map(({ message }) => message)
)
</script>

<style lang="scss" module>
.root {
  width: 600px;
  height: 100%;
  padding: 22px;

  &__close {
    display: block;
    width: 12px;
    margin-left: auto;
  }

  &__content {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    margin-top: 24px;
  }
}

.header {
  &__text {
    font-size: 18px;
    font-weight: bold;
    text-align: center;
  }
}

.content {
  &__input {
    width: 320px;
    height: 48px;
    border: solid 1px $color-border-low-emphasis;
    border-radius: $border-radius-rounder;
    font-size: 14px;
  }

  &__annotation {
    margin-top: 8px;
    font-size: 12px;
  }

  &__button {
    margin-top: 24px;
  }

  &__popover {
    position: absolute;
    transform: translateY(-120%);
  }
}

.popover {
  &__message {
    display: flex;
    align-items: center;

    &--valid {
      color: $color-success-object;
    }

    &--invalid {
      color: $color-caution-accent;
    }
  }

  &__icon {
    margin-right: 0.5em;
  }
}
</style>
